import {
  Box,
  type SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  type Theme,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { formatAsInt, formatTwoDecimals } from '@/utils/numberFormatter';
import { styles } from './styles';
import type { ProductionData, ProductionTableViewProps } from './types';

// Helper functions for formatting
const formatPercentage = (value: number | null | undefined) => {
  if (!value) return '0%';

  const isWholeNumber = value % 1 === 0;
  const formattedValue = isWholeNumber ? formatAsInt(value) : formatTwoDecimals(value);
  return `${formattedValue}%`;
};

// Historical Production Table Component
const HistoricalProductionTable = ({
  data,
  years,
}: {
  data: ProductionData[];
  years: number[];
}) => {
  const hasOilProduction = data.some(item => item.oilProductionsSum && item.oilProductionsSum > 0);
  const hasCondensateProduction = data.some(
    item => item.condensateProductionsSum && item.condensateProductionsSum > 0,
  );

  return (
    <Box sx={{ marginBottom: 2 }}>
      <TableContainer sx={styles.tableContainer || {}}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.tableHeader || {}}></TableCell>
              {years.map(year => (
                <TableCell key={year} sx={styles.tableHeader || {}}>
                  {year}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Oil Row - Only show if there's oil production */}
            {hasOilProduction && (
              <TableRow
                sx={{
                  backgroundColor: theme => theme.palette.baseColors.gray.c50,
                  borderTop: '1px solid #e0e0e0',
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <TableCell sx={styles.metricCell || {}}>Oil (bbl/d)</TableCell>
                {years.map(year => {
                  const yearData = data.find(item => item.year === year);
                  return (
                    <TableCell key={year} align='right' sx={styles.dataCell || {}}>
                      {(yearData?.oilProductionsSum || 0).toLocaleString()}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {/* Condensate Row - Only show if there's condensate production */}
            {hasCondensateProduction && (
              <TableRow
                sx={{
                  backgroundColor: theme => theme.palette.baseColors.gray.c50,
                  borderTop: '1px solid #e0e0e0',
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                <TableCell sx={styles.metricCell || {}}>Condensate (bbl/d)</TableCell>
                {years.map(year => {
                  const yearData = data.find(item => item.year === year);
                  return (
                    <TableCell key={year} align='right' sx={styles.dataCell || {}}>
                      {(yearData?.condensateProductionsSum || 0).toLocaleString()}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}

            {/* Total Oil/Condensate Row */}
            <TableRow
              sx={{
                backgroundColor: theme => theme.palette.baseColors.gray.c50,
                borderTop: '1px solid #e0e0e0',
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <TableCell sx={styles.metricCellBold || {}}>Total Oil/Condensate (bbl/d)</TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell key={year} align='right' sx={styles.dataCellBold || {}}>
                    {(yearData?.liquidsProductionsSum || 0).toLocaleString()}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Wellhead Gas Row */}
            <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.gray.c50 }}>
              <TableCell sx={styles.metricCell || {}}>Wellhead Gas (MMcf/d)</TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell key={year} align='right' sx={styles.dataCell || {}}>
                    {(yearData?.wellheadGasProductionsSum || 0).toLocaleString()}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Gas Monetization % Row */}
            <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.gray.c50 }}>
              <TableCell sx={{ ...styles.metricCellGray, fontStyle: 'italic' }}>
                Gas Monetization %
              </TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell
                    key={year}
                    align='right'
                    sx={{ ...styles.dataCellGray, fontStyle: 'italic' }}
                  >
                    {formatPercentage(yearData?.gasMonetizationPercentage)}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Sales Gas (MMcf/d) Row */}
            <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.white.c100 }}>
              <TableCell sx={styles.metricCell || {}}>Sales Gas (MMcf/d)</TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell key={year} align='right' sx={styles.dataCell || {}}>
                    {(yearData?.gasProductionsSum || 0).toLocaleString()}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Sales Gas (boe/d) Row */}
            <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.gray.c50 }}>
              <TableCell sx={styles.metricCellBold || {}}>Sales Gas (boe/d)</TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell key={year} align='right' sx={styles.dataCellBold || {}}>
                    {(yearData?.salesGasBoeSum || 0).toLocaleString()}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Empty Row */}
            <TableRow sx={{ height: '2px' }}>
              <TableCell
                colSpan={years.length + 1}
                sx={{ border: 'none', padding: 0, height: '2px' }}
              />
            </TableRow>

            {/* Total Sales Production (boe/d) Row */}
            <TableRow
              sx={{
                backgroundColor: theme => theme.palette.baseColors.gray.c50,
                borderTop: '1px solid #e0e0e0',
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <TableCell sx={styles.metricCellBold || {}}>Total Sales Production (boe/d)</TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell key={year} align='right' sx={styles.dataCellBold || {}}>
                    {(yearData?.totalProductionsSum || 0).toLocaleString()}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Year-on-year growth rate Row */}
            <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.gray.c50 }}>
              <TableCell sx={{ ...styles.metricCell, fontStyle: 'italic' }}>
                Year-on-year growth rate
              </TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell
                    key={year}
                    align='right'
                    sx={{ ...styles.dataCell, fontStyle: 'italic' }}
                  >
                    {formatPercentage(yearData?.growthRate)}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Gas/Oil Ratio Row */}
            <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.white.c100 }}>
              <TableCell sx={styles.metricCell || {}}>Gas/Oil Ratio (GOR, scf/bbl)</TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell key={year} align='right' sx={styles.dataCell || {}}>
                    {(yearData?.gasOilRatio || 0).toLocaleString()}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Forecast Production Table Component
const ForecastProductionTable = ({ data, years }: { data: ProductionData[]; years: number[] }) => {
  const hasOilPdpPud = data.some(
    item =>
      (item.pdpOilProductionsSum && item.pdpOilProductionsSum > 0) ||
      (item.udOilProductionsSum && item.udOilProductionsSum > 0),
  );
  const hasCondensatePdpPud = data.some(
    item =>
      (item.pdpCondensateProductionsSum && item.pdpCondensateProductionsSum > 0) ||
      (item.udCondensateProductionsSum && item.udCondensateProductionsSum > 0),
  );
  const hasOilPdp = data.some(item => item.pdpOilProductionsSum && item.pdpOilProductionsSum > 0);
  const hasOilPud = data.some(item => item.udOilProductionsSum && item.udOilProductionsSum > 0);
  const hasCondensatePdp = data.some(
    item => item.pdpCondensateProductionsSum && item.pdpCondensateProductionsSum > 0,
  );
  const hasCondensatePud = data.some(
    item => item.udCondensateProductionsSum && item.udCondensateProductionsSum > 0,
  );

  return (
    <Box sx={{ marginBottom: 2 }}>
      <TableContainer sx={styles.tableContainer || {}}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.tableHeader || {}}></TableCell>
              {years.map(year => (
                <TableCell key={year} sx={styles.tableHeader || {}}>
                  {year}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Oil Production Rows - Only if there's oil data */}
            {hasOilPdpPud && (
              <>
                {/* Oil - PDP Row - Only show if there's PDP oil data */}
                {hasOilPdp && (
                  <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.white.c100 }}>
                    <TableCell sx={styles.metricCellGray || {}}>Oil - PDP (bbl/d)</TableCell>
                    {years.map(year => {
                      const yearData = data.find(item => item.year === year);
                      return (
                        <TableCell key={year} align='right' sx={styles.dataCellGray || {}}>
                          {(yearData?.pdpOilProductionsSum || 0).toLocaleString()}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}

                {/* Oil - Undeveloped Row - Only show if there's PUD oil data */}
                {hasOilPud && (
                  <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.gray.c50 }}>
                    <TableCell sx={styles.metricCell || {}}>Oil - Undeveloped (bbl/d)</TableCell>
                    {years.map(year => {
                      const yearData = data.find(item => item.year === year);
                      return (
                        <TableCell key={year} align='right' sx={styles.dataCell || {}}>
                          {(yearData?.udOilProductionsSum || 0).toLocaleString()}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}

                {/* Total Oil Row */}
                <TableRow
                  sx={{
                    backgroundColor: theme => theme.palette.baseColors.gray.c50,
                    borderTop: '1px solid #e0e0e0',
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  <TableCell sx={styles.metricCellBoldWhite || {}}>Total Oil (bbl/d)</TableCell>
                  {years.map(year => {
                    const yearData = data.find(item => item.year === year);
                    const totalOil =
                      (yearData?.pdpOilProductionsSum || 0) + (yearData?.udOilProductionsSum || 0);
                    return (
                      <TableCell key={year} align='right' sx={styles.dataCellBoldWhite || {}}>
                        {totalOil.toLocaleString()}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </>
            )}

            {/* Condensate Production Rows - Only if there's condensate data */}
            {hasCondensatePdpPud && (
              <>
                {/* Condensate - PDP Row - Only show if there's PDP condensate data */}
                {hasCondensatePdp && (
                  <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.white.c100 }}>
                    <TableCell sx={styles.metricCellGray || {}}>Condensate - PDP (bbl/d)</TableCell>
                    {years.map(year => {
                      const yearData = data.find(item => item.year === year);
                      return (
                        <TableCell key={year} align='right' sx={styles.dataCellGray || {}}>
                          {(yearData?.pdpCondensateProductionsSum || 0).toLocaleString()}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}

                {/* Condensate - Undeveloped Row - Only show if there's PUD condensate data */}
                {hasCondensatePud && (
                  <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.gray.c50 }}>
                    <TableCell sx={styles.metricCell || {}}>
                      Condensate - Undeveloped (bbl/d)
                    </TableCell>
                    {years.map(year => {
                      const yearData = data.find(item => item.year === year);
                      return (
                        <TableCell key={year} align='right' sx={styles.dataCell || {}}>
                          {(yearData?.udCondensateProductionsSum || 0).toLocaleString()}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}

                {/* Total Condensate Row */}
                <TableRow
                  sx={{
                    backgroundColor: theme => theme.palette.baseColors.gray.c50,
                    borderTop: '1px solid #e0e0e0',
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  <TableCell sx={styles.metricCellBoldWhite || {}}>
                    Total Condensate (bbl/d)
                  </TableCell>
                  {years.map(year => {
                    const yearData = data.find(item => item.year === year);
                    const totalCondensate =
                      (yearData?.pdpCondensateProductionsSum || 0) +
                      (yearData?.udCondensateProductionsSum || 0);
                    return (
                      <TableCell key={year} align='right' sx={styles.dataCellBoldWhite || {}}>
                        {totalCondensate.toLocaleString()}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </>
            )}

            {/* Total Oil/Condensate Row */}
            <TableRow
              sx={{
                backgroundColor: theme => theme.palette.baseColors.gray.c50,
                borderTop: '1px solid #e0e0e0',
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <TableCell sx={styles.metricCellBold || {}}>Total Oil/Condensate (bbl/d)</TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell key={year} align='right' sx={styles.dataCellBold || {}}>
                    {(yearData?.liquidsProductionsSum || 0).toLocaleString()}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Wellhead Gas Row */}
            <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.gray.c50 }}>
              <TableCell sx={styles.metricCell || {}}>Wellhead Gas (MMcf/d)</TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell key={year} align='right' sx={styles.dataCell || {}}>
                    {(yearData?.wellheadGasProductionsSum || 0).toLocaleString()}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Gas Monetization % Row */}
            <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.gray.c50 }}>
              <TableCell sx={{ ...styles.metricCellGray, fontStyle: 'italic' }}>
                Gas Monetization %
              </TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell
                    key={year}
                    align='right'
                    sx={{ ...styles.dataCellGray, fontStyle: 'italic' }}
                  >
                    {formatPercentage(yearData?.gasMonetizationPercentage)}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Sales Gas - PDP Row */}
            <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.white.c100 }}>
              <TableCell sx={styles.metricCell || {}}>Sales Gas - PDP (MMcf/d)</TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell key={year} align='right' sx={styles.dataCell || {}}>
                    {(yearData?.pdpGasProductionsSum || 0).toLocaleString()}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Sales Gas - Undeveloped Row */}
            <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.gray.c50 }}>
              <TableCell sx={styles.metricCell || {}}>Sales Gas - Undeveloped (MMcf/d)</TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell key={year} align='right' sx={styles.dataCell || {}}>
                    {(yearData?.udGasProductionsSum || 0).toLocaleString()}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Total Sales Gas Row */}
            <TableRow
              sx={{
                backgroundColor: theme => theme.palette.baseColors.gray.c50,
                borderTop: '1px solid #e0e0e0',
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <TableCell sx={styles.metricCellBold || {}}>Total Sales Gas (MMcf/d)</TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                const totalSalesGas =
                  (yearData?.pdpGasProductionsSum || 0) + (yearData?.udGasProductionsSum || 0);
                return (
                  <TableCell key={year} align='right' sx={styles.dataCellBold || {}}>
                    {totalSalesGas.toLocaleString()}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Empty Row */}
            <TableRow sx={{ height: '2px' }}>
              <TableCell
                colSpan={years.length + 1}
                sx={{ border: 'none', padding: 0, height: '2px' }}
              />
            </TableRow>

            {/* Total Sales Production (boe/d) Row */}
            <TableRow
              sx={{
                backgroundColor: theme => theme.palette.baseColors.gray.c50,
                borderTop: '1px solid #e0e0e0',
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <TableCell sx={styles.metricCellBold || {}}>Total Sales Production (boe/d)</TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell key={year} align='right' sx={styles.dataCellBold || {}}>
                    {(yearData?.totalProductionsSum || 0).toLocaleString()}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Year-on-year growth rate Row */}
            <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.gray.c50 }}>
              <TableCell sx={{ ...styles.metricCell, fontStyle: 'italic' }}>
                Year-on-year growth rate
              </TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell
                    key={year}
                    align='right'
                    sx={{ ...styles.dataCell, fontStyle: 'italic' }}
                  >
                    {formatPercentage(yearData?.growthRate)}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Gas/Oil Ratio Row */}
            <TableRow sx={{ backgroundColor: theme => theme.palette.baseColors.white.c100 }}>
              <TableCell sx={styles.metricCell || {}}>Gas/Oil Ratio (GOR, scf/bbl)</TableCell>
              {years.map(year => {
                const yearData = data.find(item => item.year === year);
                return (
                  <TableCell key={year} align='right' sx={styles.dataCell || {}}>
                    {(yearData?.gasOilRatio || 0).toLocaleString()}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export const ProductionTableView = ({
  data,
  textFields,
  onTextFieldsChange,
  isPreview = false,
  sx,
}: ProductionTableViewProps) => {
  const location = useLocation();
  const isPreviewMode = isPreview || location.pathname.includes('/preview');

  const [localTextFields, setLocalTextFields] = useState({
    field1: textFields?.textField1 || '',
    field2: textFields?.textField2 || '',
  });

  // Process production data into historical and forecast tables
  const { historicalData, forecastData } = useMemo(() => {
    // Ensure data is an array
    if (!data || !Array.isArray(data) || data.length === 0) {
      return { historicalData: [], forecastData: [] };
    }

    // Separate historical and forecast data based on current year
    const currentYear = new Date().getFullYear();
    const historical = data.filter((item: ProductionData) => item.year < currentYear);
    const forecast = data.filter((item: ProductionData) => item.year >= currentYear);

    return {
      historicalData: historical,
      forecastData: forecast,
    };
  }, [data]);

  // Generate year ranges for table headers
  const historicalYears = useMemo(() => {
    return historicalData.map((item: ProductionData) => item.year).sort((a, b) => a - b);
  }, [historicalData]);

  const forecastYears = useMemo(() => {
    return forecastData.map((item: ProductionData) => item.year).sort((a, b) => a - b);
  }, [forecastData]);

  const handleTextChange = (field: 'field1' | 'field2', value: string) => {
    const newTextFields = {
      ...localTextFields,
      [field]: value,
    };
    setLocalTextFields(newTextFields);

    // Convert to the expected format for the parent component
    onTextFieldsChange({
      textField1: newTextFields.field1,
      textField2: newTextFields.field2,
    });
  };

  if (!data || data.length === 0) {
    return (
      <Box sx={sx || {}}>
        <Typography variant='body1' color='text.secondary'>
          No production data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...styles.container, ...sx } as SxProps<Theme>}>
      {!isPreviewMode && (
        <Box sx={styles.textFieldsContainer || {}}>
          <TextField
            label='Text Field 1'
            variant='outlined'
            size='small'
            value={localTextFields.field1}
            onChange={e => handleTextChange('field1', e.target.value)}
            sx={styles.textField || {}}
          />
          <TextField
            label='Text Field 2'
            variant='outlined'
            size='small'
            value={localTextFields.field2}
            onChange={e => handleTextChange('field2', e.target.value)}
            sx={styles.textField || {}}
          />
        </Box>
      )}

      {/* Render Historical Production Table */}
      {historicalData.length > 0 && (
        <HistoricalProductionTable data={historicalData} years={historicalYears} />
      )}

      {/* Render Forecast Production Table */}
      {forecastData.length > 0 && (
        <ForecastProductionTable data={forecastData} years={forecastYears} />
      )}
    </Box>
  );
};
